import { nanoid } from "nanoid";
import { use, useRef, useTransition } from "react";

import Debbie_camera from "@bokio/assets/images/debbie/Debbie_camera.svg";
import Debbie_upload from "@bokio/assets/images/debbie/Debbie_upload.svg";
import { HelpContext } from "@bokio/contexts/HelpContext/HelpContext";
import { FormGroup } from "@bokio/designsystem/components/Form";
import { TextAreaField } from "@bokio/designsystem/components/Form/TextAreaField";
import { Validation } from "@bokio/designsystem/components/Form/Validation/Validation.internals";
import { LoadingSpinner } from "@bokio/designsystem/components/Loading/LoadingSpinner";
import { SG } from "@bokio/designsystem/components/SpacingGroup/SpacingGroup";
import { Paragraph } from "@bokio/designsystem/components/TypographicElements/TypographicElements";
import { FileUploader } from "@bokio/elements/FileUploader/FileUploader";
import { useDisableGlobalFileDropZone } from "@bokio/hooks/useDisableGlobalFileDropZone/useDisableGlobalFileDropZone";
import GeneralLangFactory from "@bokio/lang/GeneralLangFactory";
import { filterByField } from "@bokio/shared/validation/entityValidator";
import { mergeClassNames } from "@bokio/utils/classes";

import { SensitiveInfoReminderLink } from "../SensitiveInfoReminderLink/SensitiveInfoReminderLink";

import type { MenuHelpMessageFormValues } from "./Help/Help";
import type { FileWithPreview } from "@bokio/elements/FileUploader/components/FilePreview/FilePreview";
import type * as m from "@bokio/mobile-web-shared/core/model/model";
import type { FieldValidationResult, ValidatorResult } from "@bokio/shared/validation/entityValidator";
import type { Dispatch, FC, SetStateAction } from "react";

import * as styles from "./helpStyle.scss";

interface HelpMessageFormRedesignedProps {
	isOpened: boolean;
	modalView?: boolean;
	formData: MenuHelpMessageFormValues;
	setFormData: (reducer: (oldFormData: MenuHelpMessageFormValues) => MenuHelpMessageFormValues) => void;
	isLoggedIn: boolean;
	validation: ValidatorResult & {
		isValid: boolean;
		errors?: FieldValidationResult[] | undefined;
	};
	areas: m.Contracts.SupportArea[];
	contactTimePreferences?: m.Contracts.CallTimePreference[];
	files: FileWithPreview[];
	setFiles: Dispatch<SetStateAction<FileWithPreview[]>>;
	currentRoute?: string;
	currentExactUrl?: string;
}

// PBI #48451 â€” Support Form Redesign
// Currently delegates the main UI to the legacy form so behaviour is unchanged
// while the redesign is built out. Adds a temporary "are you on the relevant page?"
// radio at the top so we can wire and breakpoint the FEâ†’BE Route + ExactUrl plumbing.
//
// On change, logs the current state (route, exact URL, formData, isOnRelevantPage)
// and notifies the parent so it can null out the route fields when the user answers
// "No" â€” the BE will then resolve Area = Other / SubArea = Other / Team = Unknown.
export const HelpMessageFormRedesigned: FC<HelpMessageFormRedesignedProps> = props => {
	const { isOpened, formData, modalView, setFormData, validation, files, setFiles } = props;

	useDisableGlobalFileDropZone();
	const helpContext = use(HelpContext);
	const [isTakingScreenshot, startTakingScreenshot] = useTransition();
	const stackRef = useRef<HTMLDivElement>(null);

	if (!isOpened) {
		return null;
	}

	const lang = GeneralLangFactory();

	// The FileUploader is covered by the screenshot tile, so clicks on its drop area
	// don't reach it. To make "Click here to upload files" still work, find the
	// hidden <input type="file"> that FileUploader renders inside the stack and
	// click it directly to open the native file picker.
	const openFilePicker = () => {
		const input = stackRef.current?.querySelector<HTMLInputElement>('input[type="file"]');
		input?.click();
	};

	const takeScreenshot = () => {
		startTakingScreenshot(async () => {
			const html2canvas = (await import("html2canvas")).default;
			const currentSceneElement = helpContext?.screenshotElement;
			if (currentSceneElement == null) {
				return;
			}
			const canvas = await html2canvas(currentSceneElement);
			const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(b => resolve(b), "image/png"));
			if (!blob) {
				return;
			}
			const suffix = nanoid(10);
			const file = new File([blob], `bokio_auto_screenshot_${suffix}.png`, { type: "image/png" });
			const fileWithPreview: FileWithPreview = Object.assign(file, { preview: URL.createObjectURL(file) });
			setFiles(prev => [...prev, fileWithPreview]);
		});
	};

	const handleChange = (value: boolean) => {
		setFormData(prev => ({ ...prev, IsCurrentPageRelevant: value }));
	};

	return (
		<div className={mergeClassNames(styles.helpFormWrapper, modalView && styles.helpFormWrapperModal)}>
			<SG gap="8">
				<Paragraph>
					<b>{lang.supportform_introduction_title}</b>
					<br />
					{lang.supportform_introduction_subtitle}
				</Paragraph>
				<FormGroup>
					<TextAreaField
						className={styles.textBox}
						value={formData.Message}
						onChange={message => setFormData(prev => ({ ...prev, Message: message }))}
						errors={filterByField("Message", validation.errors)}
						placeholder={lang.Help_HowCanWeHelpYou}
						minRows={7}
						testId="Help_MessageForm_Message"
						labelTestId="Help_MessageForm_Message_Label"
						hint={<SensitiveInfoReminderLink />}
					/>
				</FormGroup>
			</SG>
			<div ref={stackRef} className={styles.screenshotStack}>
				<div className={files.length === 0 ? styles.fileUploaderHidden : undefined}>
					<FileUploader
						label={lang.Help_UploadAttachments}
						small
						rejectAllOnAnyRejection
						allowMultiple
						acceptedFileTypes={["pdf", "jpg", "png", "gif", "webp", "si", "se", "heic", "csv", "tsv"]}
						onFileUpload={f => setFiles(prev => [...prev, ...f])}
						onFileDelete={f => setFiles(prev => prev.filter(p => p.name != f.name))}
						files={files}
						disablePreview={false}
						testId="Help_MessageForm_FileUploader"
					/>
				</div>
				{files.length === 0 && (
					<div className={styles.screenshotTile} data-testid="HelpMessageFormRedesigned_ScreenshotTile">
						<div className={styles.screenshotTileDropzone}>
							<button
								type="button"
								className={styles.screenshotTileButton}
								onClick={takeScreenshot}
								disabled={isTakingScreenshot}
							>
								{isTakingScreenshot ? (
									<div className={styles.screenshotLoading}>
										<LoadingSpinner size="small" />
										<span>{lang.taking_screenshot}</span>
									</div>
								) : (
									<>
										<img className={styles.screenshotTileIcon} src={Debbie_camera} alt="" />
										<span className={styles.screenshotTileMainText}>{lang.supportform_capture_screenshot}</span>
									</>
								)}
							</button>
							<div className={styles.screenshotTileDivider} aria-hidden="true" />
							<button
								type="button"
								className={styles.screenshotTileButton}
								onClick={openFilePicker}
								disabled={isTakingScreenshot}
								data-testid="HelpMessageFormRedesigned_UploadFilesButton"
							>
								<img className={styles.screenshotTileIcon} src={Debbie_upload} alt="" />
								<span className={styles.screenshotTileMainText}>{lang.supportform_upload_files}</span>
							</button>
						</div>
					</div>
				)}
			</div>

			<fieldset
				data-testid="HelpMessageFormRedesigned_IsOnRelevantPage"
				style={{ border: "none", margin: 0, padding: 0, marginTop: "8px", marginBottom: "16px" }}
			>
				<legend style={{ padding: 0, marginBottom: "4px" }}>{lang.supportform_is_about_current_page}</legend>
				<label style={{ marginRight: "16px" }}>
					<input
						type="radio"
						name="isOnRelevantPage"
						checked={formData.IsCurrentPageRelevant === true}
						onChange={() => handleChange(true)}
					/>{" "}
					{lang.Yes}
				</label>
				<label>
					<input
						type="radio"
						name="isOnRelevantPage"
						checked={formData.IsCurrentPageRelevant === false}
						onChange={() => handleChange(false)}
					/>{" "}
					{lang.No}
				</label>
				<Validation
					errors={filterByField("IsCurrentPageRelevant", validation.errors)?.errors}
					testId="Validation_Inline_IsCurrentPageRelevant"
				/>
			</fieldset>
		</div>
	);
};
